import { useCallback, useEffect, useMemo, useState } from "react";

// ==================== TYPES ====================

interface UseTablePaginationOptions {
  /** Rows per page — defaults to 10 */
  defaultRowsPerPage?: number;
  /** Available page-size options — defaults to [10, 25, 50] */
  rowsPerPageOptions?: number[];
}

interface UseTablePaginationResult<T> {
  /** Sliced rows for the current page */
  paginatedRows: T[];
  /** Zero-indexed current page */
  page: number;
  /** Current rows-per-page setting */
  rowsPerPage: number;
  /** Available page-size options (pass directly to MUI TablePagination) */
  rowsPerPageOptions: number[];
  /** Total row count (pre-pagination) */
  totalCount: number;
  /** Whether pagination controls should render (false when all rows fit on one page) */
  showPagination: boolean;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  /** Convenience handler shaped for MUI TablePagination onPageChange */
  handlePageChange: (_event: unknown, newPage: number) => void;
  /** Convenience handler shaped for MUI TablePagination onRowsPerPageChange */
  handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// ==================== HOOK ====================

/**
 * Client-side pagination for pre-fetched data arrays.
 *
 * Automatically resets to page 0 when the source data length changes
 * (e.g. new filter results, different policy selected).
 *
 * Used by:
 * - PaginatedDatagrid (client-side mode) for resource list views
 * - Claims Summary tables (SummaryTable, ClaimsDetailTable, etc.) for report views
 * - Any component that needs to paginate an in-memory array
 *
 * @example Basic usage
 * ```tsx
 * const { paginatedRows, showPagination, ...paginationProps } = useTablePagination(rows);
 *
 * return (
 *   <>
 *     <Table>
 *       <TableBody>
 *         {paginatedRows.map(row => <TableRow key={row.id}>...</TableRow>)}
 *       </TableBody>
 *     </Table>
 *     {showPagination && <TablePagination component="div" count={paginationProps.totalCount} {...paginationProps} />}
 *   </>
 * );
 * ```
 */
export const useTablePagination = <T>(
  rows: T[],
  options: UseTablePaginationOptions = {}
): UseTablePaginationResult<T> => {
  const { defaultRowsPerPage = 10, rowsPerPageOptions = [10, 25, 50] } = options;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // Reset to first page when the dataset changes size
  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const handlePageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return {
    paginatedRows,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    totalCount: rows.length,
    showPagination: rows.length > rowsPerPageOptions[0],
    setPage,
    setRowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
